import React from 'react';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { StatusPill, LegendItem } from '../../components/ui';
import type { StatusTone } from '../../components/ui';
import { focusRing, mix, sbg } from '../../theme/sbgTokens';
import { MISSING, formatNumber } from '../../utils/format';
import type { AllocationPace, PaceStatus } from '../../utils/timeOffUsage';

/* ------------------------------------------------------------------------ */
/* Time-off pace: shared vocabulary for the Employees directory              */
/* ------------------------------------------------------------------------ */

export const PACE_META: Record<PaceStatus, { label: string; tone: StatusTone | null; severity: number }> = {
  'over-allotment': { label: 'Over allotment', tone: 'danger', severity: 4 },
  'over-pace': { label: 'Pacing over', tone: 'warning', severity: 3 },
  'pending-risk': { label: 'Pending risk', tone: 'warning', severity: 2 },
  'within-pace': { label: 'Within pace', tone: null, severity: 1 },
  'no-allotment': { label: 'No allotment', tone: null, severity: 0 },
};

/** The statuses that ask HR to look (a status pill, a KPI count). */
export const ATTENTION_STATUSES: PaceStatus[] = ['over-allotment', 'over-pace', 'pending-risk'];

/** The most severe of several pace statuses. */
export const worstPace = (...statuses: PaceStatus[]): PaceStatus =>
  statuses.reduce((worst, status) => (PACE_META[status].severity > PACE_META[worst].severity ? status : worst), 'no-allotment' as PaceStatus);

/** 24 · 7.5 · −16 — hours with at most one decimal and a true minus, through the shared formatter. */
export const formatHoursValue = (value: number) => {
  const rounded = Math.round(Math.abs(value) * 10) / 10;
  const magnitude = formatNumber(rounded, Number.isInteger(rounded) ? 0 : 1);
  return value < 0 && rounded > 0 ? `−${magnitude}` : magnitude;
};

/** 24 h */
export const formatHours = (value: number) => `${formatHoursValue(value)} h`;

const SCHEDULED_FILL = mix(sbg.action.primary, 40);
const PENDING_FILL = `repeating-linear-gradient(-30deg, ${sbg.action.primary} 0 1.5px, ${mix(sbg.action.primary, 18)} 1.5px 4px)`;

const segmentWidths = (pace: AllocationPace) => {
  const used = pace.allotment > 0 ? Math.min(pace.usedPercent, 100) : pace.used > 0 ? 100 : 0;
  const scheduled = pace.allotment > 0
    ? Math.min(pace.scheduledPercent, Math.max(100 - used, 0))
    : pace.used === 0 && pace.scheduled > 0 ? 100 : 0;
  const pending = pace.allotment > 0
    ? Math.min(pace.pendingPercent, Math.max(100 - used - scheduled, 0))
    : pace.used === 0 && pace.scheduled === 0 && pace.pending > 0 ? 100 : 0;
  return { used, scheduled, pending };
};

const TooltipLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="caption" component="span" sx={{ color: 'inherit', opacity: 0.8 }}>{label}</Typography>
    <Typography variant="caption" component="span" sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

/** "Left after approved 16 h", or "Over allotment by 16 h" once approved time passes the allotment. */
const RemainingLine: React.FC<{ pace: AllocationPace }> = ({ pace }) =>
  pace.available < 0 ? (
    <TooltipLine label="Over allotment by" value={formatHours(-pace.available)} />
  ) : (
    <TooltipLine label="Left after approved" value={formatHours(pace.available)} />
  );

/**
 * One allotment in a table cell: approved hours (taken + scheduled) over the
 * allotment — the same total the status is judged on — above a 6px stacked
 * bar: taken, then scheduled, then pending (hatched), with a tick at the point
 * the employee should have reached by today. Details on hover/focus.
 */
export const PaceCell: React.FC<{
  /** "PTO", "Sick", "PTO and sick" — names the allotment for tooltips and screen readers. */
  kind: string;
  pace: AllocationPace;
  loading: boolean;
  /** Show the skeleton (true once loading has lasted 300ms). */
  showSkeleton: boolean;
  error: boolean;
  /**
   * Pace (the "should be here today" tick, the Dec 31 projection, the pace
   * status) only means something for people still employed. Former rows pass false.
   */
  showPace?: boolean;
}> = ({ kind, pace, loading, showSkeleton, error, showPace = true }) => {
  if (loading) {
    return (
      <Box aria-hidden="true" sx={{ width: 96, ml: 'auto', display: 'grid', gap: 0.75, justifyItems: 'end', visibility: showSkeleton ? 'visible' : 'hidden' }}>
        <Skeleton variant="rounded" width={64} height={14} />
        <Skeleton variant="rounded" width="100%" height={6} />
      </Box>
    );
  }

  if (error) {
    return (
      <Tooltip title={`${kind} usage couldn’t be loaded, so no estimate is shown.`}>
        <Typography
          component="span"
          variant="body1"
          role="img"
          tabIndex={0}
          aria-label={`${kind} usage unavailable`}
          sx={{ color: sbg.text.secondary, borderRadius: sbg.radius.xs, '&:focus-visible': focusRing }}
        >
          {MISSING}
        </Typography>
      </Tooltip>
    );
  }

  const meta = PACE_META[pace.status];
  const widths = segmentWidths(pace);
  const overBy = pace.available < 0 ? ` Over allotment by ${formatHours(-pace.available)}.` : '';
  const summary =
    `${kind}: ${formatHoursValue(pace.committed)} of ${formatHours(pace.allotment)} approved ` +
    `(${formatHours(pace.used)} taken, ${formatHours(pace.scheduled)} scheduled), ${formatHours(pace.pending)} pending.` +
    (showPace ? ` ${meta.label}.` : overBy);

  return (
    <Tooltip
      placement="top"
      title={
        <Box sx={{ display: 'grid', gap: 0.25, minWidth: 200 }}>
          <Typography variant="caption" component="p" sx={{ color: 'inherit', fontWeight: 600, mb: 0.25 }}>
            {showPace ? `${kind} · ${meta.label}` : kind}
          </Typography>
          <TooltipLine label="Taken" value={formatHours(pace.used)} />
          <TooltipLine label="Scheduled" value={formatHours(pace.scheduled)} />
          <TooltipLine label="Pending approval" value={formatHours(pace.pending)} />
          <RemainingLine pace={pace} />
          {showPace && <TooltipLine label="Projected by Dec 31" value={formatHours(pace.projected)} />}
        </Box>
      }
    >
      <Box
        role="img"
        tabIndex={0}
        aria-label={summary}
        sx={{ width: 96, ml: 'auto', display: 'grid', gap: 0.75, borderRadius: sbg.radius.xs, '&:focus-visible': focusRing }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary }}>
            {formatHoursValue(pace.committed)}
          </Typography>
          <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
            / {formatHours(pace.allotment)}
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', height: 6 }}>
          <Box sx={{ position: 'absolute', inset: 0, borderRadius: sbg.radius.pill, bgcolor: sbg.bg.track, overflow: 'hidden', display: 'flex' }}>
            <Box sx={{ width: `${widths.used}%`, bgcolor: sbg.action.primary }} />
            <Box sx={{ width: `${widths.scheduled}%`, bgcolor: SCHEDULED_FILL }} />
            <Box sx={{ width: `${widths.pending}%`, background: PENDING_FILL }} />
          </Box>
          {showPace && pace.allotment > 0 && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                top: -2,
                height: 10,
                width: 2,
                borderRadius: 1,
                bgcolor: sbg.text.primary,
                left: `calc(${pace.elapsedPercent}% - 1px)`,
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

/**
 * The row's pace: a status pill only when HR should look (over allotment,
 * pacing over, pending risk), otherwise a quiet word so exceptions stand out,
 * with the combined approved PTO + sick total underneath.
 */
export const PaceStatusCell: React.FC<{
  status: PaceStatus;
  /** Combined PTO + sick allotment. */
  total: AllocationPace;
  /** "PTO: within pace · Sick: over allotment" */
  detail: string;
  loading: boolean;
  showSkeleton: boolean;
  error: boolean;
}> = ({ status, total, detail, loading, showSkeleton, error }) => {
  if (loading) {
    return (
      <Box aria-hidden="true" sx={{ display: 'grid', gap: 0.75, visibility: showSkeleton ? 'visible' : 'hidden' }}>
        <Skeleton variant="rounded" width={88} height={20} sx={{ borderRadius: sbg.radius.pill }} />
        <Skeleton variant="rounded" width={72} height={12} />
      </Box>
    );
  }
  if (error) {
    return <Typography component="span" variant="body1" sx={{ color: sbg.text.secondary }}>{MISSING}</Typography>;
  }
  const meta = PACE_META[status];
  const totalLabel = `${formatHoursValue(total.committed)} / ${formatHours(total.allotment)} total`;
  const totalSpoken = `${formatHoursValue(total.committed)} of ${formatHours(total.allotment)} approved in total`;
  return (
    <Tooltip
      placement="top"
      title={
        <Box sx={{ display: 'grid', gap: 0.25, minWidth: 200 }}>
          <Typography variant="caption" component="p" sx={{ color: 'inherit', fontWeight: 600, mb: 0.25 }}>
            PTO and sick · {PACE_META[total.status].label}
          </Typography>
          <TooltipLine label="Taken" value={formatHours(total.used)} />
          <TooltipLine label="Scheduled" value={formatHours(total.scheduled)} />
          <TooltipLine label="Pending approval" value={formatHours(total.pending)} />
          <TooltipLine label="Projected by Dec 31" value={formatHours(total.projected)} />
          <Typography variant="caption" component="p" sx={{ color: 'inherit', opacity: 0.8, mt: 0.5 }}>
            {detail}
          </Typography>
        </Box>
      }
    >
      <Box
        role="img"
        tabIndex={0}
        aria-label={`${meta.label}. ${detail}. ${totalSpoken}.`}
        sx={{ display: 'inline-grid', gap: 0.5, justifyItems: 'start', borderRadius: sbg.radius.sm, '&:focus-visible': focusRing }}
      >
        {meta.tone ? (
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        ) : (
          <Typography component="span" variant="body1" sx={{ color: sbg.text.primary, whiteSpace: 'nowrap' }}>
            {meta.label}
          </Typography>
        )}
        <Typography component="span" variant="body2" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {totalLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/** Legend for the pace bars (footer of the table). The today tick only appears where pace applies. */
export const PaceLegend: React.FC<{ showPace?: boolean }> = ({ showPace = true }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 0.5 }}>
    <LegendItem color={sbg.action.primary} label="Taken" />
    <LegendItem color={SCHEDULED_FILL} label="Scheduled" />
    <LegendItem color={sbg.action.primary} hatched label="Pending approval" />
    {showPace && (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Box aria-hidden="true" sx={{ width: 2, height: 12, borderRadius: 1, bgcolor: sbg.text.primary }} />
        <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
          Where they should be today
        </Typography>
      </Box>
    )}
  </Box>
);
